import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryApi, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { accessToken } from './accessToken';
import { prepareHeaders } from './prepareHeaders';
import { accessTokenStored } from './tokenSlice';

const rawBaseQuery = fetchBaseQuery({ baseUrl: `${import.meta.env.VITE_BACKEND_URI}`, prepareHeaders });

const EXPIRY_BUFFER_SECONDS = 30;

const isTokenExpired = (token: string | null): boolean => {
    if (!token) {
        return true;
    }

    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return false; // Not a JWT — can't determine expiry, assume valid
        }
        const payload = JSON.parse(atob(parts[1]));
        if (!payload.exp) {
            return false; // No exp claim — assume valid
        }
        const expiresAt = payload.exp * 1000;
        return Date.now() >= expiresAt - EXPIRY_BUFFER_SECONDS * 1000;
    } catch {
        return false; // Can't decode — assume valid, let the request proceed
    }
};

// Shared promise to deduplicate concurrent refresh requests.
// Only the first caller triggers the actual refresh; all others await the same promise.
let refreshPromise: Promise<void> | null = null;

const refreshToken = async (api: BaseQueryApi): Promise<void> => {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            try {
                // Lazy import to avoid circular dependency:
                // authenticatedBaseQuery -> MessageHandler -> store(@/configuration/setup/store) -> authenticatedBaseQuery
                const { requestTokenRefresh } = await import('@/features/app/MessageHandler');
                const newToken = await requestTokenRefresh();
                accessToken.saveAccessToken(newToken);
                api.dispatch(accessTokenStored(newToken));
            } finally {
                refreshPromise = null;
            }
        })();
    }
    return refreshPromise;
};

export const authenticatedBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions,
) => {
    const currentToken = accessToken.getAccessToken();

    // Proactive refresh: if the token is about to expire, refresh before making the request
    if (currentToken && isTokenExpired(currentToken)) {
        try {
            await refreshToken(api);
        } catch {
            // Token refresh failed — proceed with the expired token, will likely get 401
        }
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    // Reactive retry: if the server returned 401 despite our check, attempt one refresh + retry
    if (result.error && (result.error as FetchBaseQueryError).status === 401) {
        try {
            await refreshToken(api);
            return await rawBaseQuery(args, api, extraOptions);
        } catch {
            // Refresh failed on retry — return the original 401 error
        }
    }

    return result;
};
